import React, { useState, useEffect } from 'react';
import { BarChart3, Filter, Download, TrendingUp, Building2, CheckCircle, XCircle, Clock } from 'lucide-react';
import ExecutiveSummaryCard from './ExecutiveSummaryCard';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

const API_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

interface ReportStats {
  total: number;
  pending: number;
  tl_approved: number;
  pm_approved: number;
  deployed: number;
  rejected: number;
  byModule: Record<string, number>;
  byInstitute: Record<string, number>;
}

interface ReportData {
  deployments: any[];
  stats: ReportStats;
  filters: any;
}

interface Module {
  _id: string;
  name: string;
}

interface Institute {
  _id: string;
  name: string;
}

const DeploymentReports: React.FC = () => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState<Module[]>([]);
  const [institutes, setInstitutes] = useState<Institute[]>([]);
  
  // Filters
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedInstitute, setSelectedInstitute] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('');
  const [selectedDeploymentType, setSelectedDeploymentType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const isReleaseNoteApplicable = (deployment: any) =>
    !['db-script', 'new-deployment'].includes(deployment?.deploymentType || '');

  useEffect(() => {
    fetchModules();
    fetchInstitutes();
    fetchReport();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/modules`);
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) setModules(data);
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  };

  const fetchInstitutes = async () => {
    try {
      const response = await fetch(`${API_URL}/api/institutes`);
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) setInstitutes(data);
    } catch (error) {
      console.error('Error fetching institutes:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (period && period !== 'custom') {
        params.append('period', period);
      } else if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      
      if (selectedModule) params.append('module', selectedModule);
      if (selectedInstitute) params.append('institute', selectedInstitute);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedPriority) params.append('priority', selectedPriority);
      if (selectedDeploymentType) params.append('deploymentType', selectedDeploymentType);
      
      const response = await fetch(`${API_URL}/api/reports/deployments?${params}`);
      if (!response.ok) {
        toast(await readApiError(response, 'Failed to load report'), 'error');
        return;
      }
      const data = await response.json();
      if (data && Array.isArray(data.deployments)) setReportData(data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast('Failed to load report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchReport();
  };

  const handleExportCSV = () => {
    if (!reportData) return;
    
    // Helper function to convert UTC to IST with DD/MM/YYYY format
    const toIST = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', { 
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(',', '');
    };

    const csvData = reportData.deployments.map(d => {
      // Get TL approvals chain
      const tlApprovals = Array.isArray(d.tlApprovals) 
        ? d.tlApprovals
            .filter((tl: any) => tl.approved && tl.tl)
            .map((tl: any) => `${tl.tl.name} (${toIST(tl.approvedAt)})`)
            .join('; ')
        : '';

      // Get PM approvals chain
      const pmApprovals = Array.isArray(d.pmApprovals)
        ? d.pmApprovals
            .filter((pm: any) => pm.approved && pm.pm)
            .map((pm: any) => `${pm.pm.name} (${toIST(pm.approvedAt)})`)
            .join('; ')
        : d.pmApproval?.approvedBy ? `${d.pmApproval.approvedBy.name} (${toIST(d.pmApproval.approvedAt)})` : '';

      // Get Tester info (handle both testers array and single tester)
      const testerInfo = Array.isArray(d.testers) && d.testers.length > 0
        ? d.testers.map((t: any) => t.name || t.username || t.email || 'Unknown').join('; ')
        : d.tester ? (d.tester.name || d.tester.username || d.tester.email || 'Unknown') : '';

      return {
        'Deployment Number': d.deploymentNumber !== undefined ? `#${d.deploymentNumber}` : '',
        'Title': d.title || '',
        'Description': d.description || '',
        'Release Note': isReleaseNoteApplicable(d) ? (d.releaseNote || '') : '',
        'Status': d.status || '',
        'Priority': d.priority || '',
        'Deployment Type': d.deploymentType || '',
        'Requested By': d.requestedBy?.name || '',
        'Requested At': toIST(d.requestedAt),
        'Scheduled Date': d.scheduledDate || '',
        'Modules': d.modules?.map((m: any) => m.name).join('; ') || d.module?.name || '',
        'Institutes': d.institutes?.map((i: any) => i.name).join('; ') || d.institute?.name || '',
        'TL Approvals': tlApprovals,
        'PM Approvals': pmApprovals,
        'Tester': testerInfo,
        'Deployed By': d.deployment?.deployedBy?.name || '',
        'Deployed At': toIST(d.deployment?.deployedAt),
        'DB Script': d.dbScriptFileName ? 'Yes' : 'No'
      };
    });
    
    const headers = Object.keys(csvData[0]);
    const csv = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header as keyof typeof row] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Add timestamp to filename in IST
    const timestamp = new Date().toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/[/,:\s]/g, '-');
    a.download = `deployment-report-${timestamp}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const stats = reportData?.stats;

  const periodLabel = period === 'custom' && startDate && endDate
    ? `${startDate} – ${endDate}`
    : { today: 'Today', week: 'Last 7 Days', month: 'Last 30 Days', year: 'Last Year' }[period] || 'All Time';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            Deployment Reports
          </h1>
          <p className="text-gray-600 mt-1">Analyze deployment trends and statistics</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
          <button
            onClick={handleExportCSV}
            disabled={!reportData}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            Filter Options
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Period
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="year">Last Year</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Range */}
            {period === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            {/* Module */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Module
              </label>
              <select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Modules</option>
                {modules.map(m => (
                  <option key={m._id} value={m._id}>{m.name}</option>
                ))}
              </select>
            </div>

            {/* Institute */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Institute
              </label>
              <select
                value={selectedInstitute}
                onChange={(e) => setSelectedInstitute(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Institutes</option>
                {institutes.map(i => (
                  <option key={i._id} value={i._id}>{i.name}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="tl_approved">TL Approved</option>
                <option value="pm_approved">PM Approved</option>
                <option value="deployed">Deployed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={selectedPriority}
                onChange={(e) => setSelectedPriority(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="high">🔴 High</option>
                <option value="medium">🟡 Medium</option>
                <option value="low">🟢 Low</option>
              </select>
            </div>

            {/* Deployment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deployment Type
              </label>
              <select
                value={selectedDeploymentType}
                onChange={(e) => setSelectedDeploymentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="bug">🐛 Bug</option>
                <option value="feature">✨ Feature</option>
                <option value="payload">📦 Payload</option>
                <option value="version">🔢 Version</option>
                <option value="new-deployment">🚀 New Deployment</option>
                <option value="db-script">🗄️ DB Script</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={() => {
                setPeriod('month');
                setStartDate('');
                setEndDate('');
                setSelectedModule('');
                setSelectedInstitute('');
                setSelectedStatus('');
                setSelectedPriority('');
                setSelectedDeploymentType('');
                fetchReport();
              }}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset Filters
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Deployments</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Deployed</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.deployed}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats.total > 0 ? ((stats.deployed / stats.total) * 100).toFixed(1) : 0}% of total
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Pending</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.pending}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats.total > 0 ? ((stats.pending / stats.total) * 100).toFixed(1) : 0}% of total
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm font-medium">Rejected</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stats.rejected}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {stats.total > 0 ? ((stats.rejected / stats.total) * 100).toFixed(1) : 0}% of total
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Executive Summary: KPI pills + top modules bar + status donut */}
      {stats && <ExecutiveSummaryCard stats={stats} periodLabel={periodLabel} />}

      {/* By Institute */}
      {stats && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            Deployments by Institute
          </h3>
          <div className="space-y-3">
            {Object.keys(stats.byInstitute).length > 0 ? (
              Object.entries(stats.byInstitute)
                .sort(([, a], [, b]) => b - a)
                .map(([institute, count]) => (
                  <div key={institute} className="flex items-center justify-between">
                    <span className="text-gray-700">{institute}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-violet-600 h-2 rounded-full"
                          style={{ width: `${(count / stats.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-900 w-8">{count}</span>
                    </div>
                  </div>
                ))
            ) : (
              <p className="text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </div>
      )}

      {/* Release Notes Section */}
      {reportData && reportData.deployments.some((d: any) => isReleaseNoteApplicable(d) && d.releaseNote) && (
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6 border-b border-gray-200 flex items-center gap-2">
            <span className="text-xl">📋</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Release Notes</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                {reportData.deployments.filter((d: any) => isReleaseNoteApplicable(d) && d.releaseNote).length} deployment{reportData.deployments.filter((d: any) => isReleaseNoteApplicable(d) && d.releaseNote).length !== 1 ? 's' : ''} with release notes
              </p>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {reportData.deployments
              .filter((d: any) => isReleaseNoteApplicable(d) && d.releaseNote)
              .map((d: any) => (
                <div key={d._id || d.id} className="p-5 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-start gap-3">
                    {/* Left: deployment info */}
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                        #{d.deploymentNumber || '—'}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        d.status === 'deployed' ? 'bg-green-100 text-green-800' :
                        d.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        d.status === 'pm_approved' ? 'bg-blue-100 text-blue-800' :
                        d.status === 'tl_approved' ? 'bg-purple-100 text-purple-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {d.status?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{d.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {d.requestedBy?.name} &bull; {new Date(d.requestedAt).toLocaleDateString('en-GB')} &bull; {d.modules?.map((m: any) => m.name).join(', ') || '—'}
                      </p>
                      {/* Release note */}
                      <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <p className="text-xs font-semibold text-amber-700 uppercase mb-1">Release Note</p>
                        {d.releaseNote.startsWith('http') ? (
                          <a
                            href={d.releaseNote}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm break-all"
                          >
                            🔗 {d.releaseNote}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{d.releaseNote}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Deployment List */}
      {reportData && (
        <div className="bg-white rounded-xl shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Deployment Details</h3>
            <p className="text-sm text-gray-600 mt-1">
              Showing {reportData.deployments.length} deployment{reportData.deployments.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modules</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institutes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Release Note</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reportData.deployments.map((deployment) => (
                  <tr key={deployment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500">
                      {deployment.deploymentNumber ? `#${deployment.deploymentNumber}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {deployment.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        deployment.status === 'deployed' ? 'bg-green-100 text-green-800' :
                        deployment.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        deployment.status === 'pm_approved' ? 'bg-blue-100 text-blue-800' :
                        deployment.status === 'tl_approved' ? 'bg-purple-100 text-purple-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {deployment.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {deployment.requestedBy?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {new Date(deployment.requestedAt).toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {deployment.modules?.map((m: any) => m.name).join(', ') || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {deployment.institutes?.map((i: any) => i.name).join(', ') || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm max-w-xs">
                      {isReleaseNoteApplicable(deployment) && deployment.releaseNote ? (
                        deployment.releaseNote.startsWith('http') ? (
                          <a
                            href={deployment.releaseNote}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline break-all"
                          >
                            🔗 {deployment.releaseNote}
                          </a>
                        ) : (
                          <span className="text-gray-700 break-words">{deployment.releaseNote}</span>
                        )
                      ) : (
                        <span className="text-gray-400 italic">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default DeploymentReports;
